// START IMPORTS
import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack, useTheme } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  DateUtil,
  usePhoneFormatter,
} from '@ltvco/refresh-lib/utils';
import { DebugMenu, ReportFactory, ReportLoading } from '@ltvco/refresh-lib/v1';
import {
  FraudPhoneOverview,
  FraudPhoneThreatDetection,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  ReportActionsWithDateUpdated,
  ReportRating,
  PotentialOwnerCta as PotentialOwnerCtaV2,
  ReportNullState,
  CommentsSection,
  usePhoneComments,
} from '@ltvco/refresh-lib/v2';
import { useContext, useState } from 'react';

import { getFraudPhoneNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';

import type { ReportRouteProps } from '../types';
// END IMPORTS

export function PhoneFraudReportPage({ permalink }: ReportRouteProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const theme = useTheme();
  const { isHidden } = useSubscriptionAccessConfigContext();
  const { logError } = useContext(AppConfig);

  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);

  const { commentsQuery } = usePhoneComments({
    permalink,
    phoneNumber: rawPhone,
    logError,
    enabled: queryResult.isSuccess,
  });

  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const report = ReportFactory.create(queryResult?.data, 'phone-fraud');

  if (!report) return <ReportLoading menuItems={2} />;
  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }

  const reportType = 'phone-fraud';
  const {
    fraudScore,
    isActive,
    activeStatus,
    isDoNotCall,
    isPrepaid,
    isRecentAbuse,
    isRisky,
    isSpammer,
    isVOIP,
    isValid,
    isLeaked,
    lineType,
    location,
    userActivity,
    spamReportsCount,
    spamScore,
    num_searches,
    carrier,
  } = report.data;

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  document.title = `${formattedPhoneNumber} - BeenVerified`;
  const commentsLength = (commentsQuery.data?.comment_count as number) ?? 0;
  const fraudPhoneNavLinkDataV2 = getFraudPhoneNavLinkDataV2({
    commentsLength,
  });
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Fraud Scan"
            headerTitle={formattedPhoneNumber}
            menuItems={
              fraudPhoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0}>
          <Box
            sx={{
              height: 30,
              backgroundColor: theme.palette.background.default,
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Phone Fraud"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <FraudPhoneOverview
            isDoNotCall={isDoNotCall}
            isVOIP={isVOIP}
            isPrepaid={isPrepaid}
            isValid={isValid}
            isActive={isActive}
            isLeaked={isLeaked}
            activeStatus={activeStatus}
            userActivity={userActivity}
            number={formattedPhoneNumber}
            location={location?.address?.full || ''}
            parsedLocation={location?.address?.parsed}
            timezone={location?.timezone || ''}
            carrier={carrier}
            fraudScore={fraudScore || 0}
            lineType={lineType}
            isRisky={isRisky}
            isSpammer={isSpammer}
            isRecentAbuse={isRecentAbuse}
          />

          <FraudPhoneThreatDetection
            fraudScore={fraudScore ?? 0}
            isRisky={isRisky}
            isRecentAbuse={isRecentAbuse}
            isSpammer={isSpammer}
            permalink={permalink}
            spam={spam}
            setSpam={setSpam}
            searchCount={num_searches}
            spamScore={spamScore}
            spamReportsCount={spamReportsCount}
            userComments={commentsLength}
            userActivity={userActivity}
          />

          <Stack spacing={2}>
            {!isHidden('phone') && (
              <PotentialOwnerCtaV2
                number={formattedPhoneNumber}
                reportType="phone"
              />
            )}
            <CommentsSection
              comments={commentsQuery.data?.comments ?? []}
              reportData={report.data}
              permalink={permalink}
              formattedPhoneNumber={formattedPhoneNumber}
              reportType={reportType}
            />
            <ReportRating
              rating={report.data.meta?.rating || null}
              report_type={reportType}
            />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Fraud Scan"
        headerTitle={formattedPhoneNumber}
        menuItems={
          fraudPhoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
        }
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
  // END RENDER
}
